<script lang="ts" setup>
  import { type FunctionalComponent, ref, toRefs } from 'vue'
  import Popover from 'primevue/popover'
  import Checkbox from 'primevue/checkbox'
  import ButtonDefault from '@/elements/ButtonDefault.vue'

  export interface DropdownButtonItem {
    label: string
    value?: string | number | boolean | object | (() => unknown),
    icon?: string
    disabled?: boolean
    command?: (() => unknown)
  }

  interface DropdownButtonProps {
    values: DropdownButtonItem[]
    icon?: FunctionalComponent | MaterialIconVueType | false | undefined
    size?: 'small' | 'large' | 'extra-small' | undefined
    multiselect?: boolean
    severity?: string
    functionKey?: string
    label?: string
    selectedItemIcon?: string
  }

  const modelValue = defineModel<string | number | boolean | object | DropdownButtonItem[]>({ required: false })

  const props = defineProps<DropdownButtonProps>()
  const { values, icon, size, multiselect, severity, functionKey, label, selectedItemIcon } = toRefs(props)

  const emit = defineEmits(['update:modelValue', 'buttonClick'])

  const op = ref()

  const fnKey = functionKey?.value ?? 'value'

  const toggle = (event: Event) => {
    op.value.toggle(event)
    emit('buttonClick')
  }

  const onListitemClick = (item: DropdownButtonItem) => {
    const fn = item[fnKey]

    if (typeof fn === 'function') {
      fn()
      op.value.hide()
      return
    }

    if (item.command) {
      item.command()
      op.value.hide()
      return
    }

    if (!multiselect.value) {
      modelValue.value = fn
      op.value.hide()
      emit('update:modelValue', modelValue.value)
      return
    }

    if (modelValue.value)
      modelValue.value[fn as string] = !modelValue.value[fn as string]

    emit('update:modelValue', modelValue.value)
  }

  const isSelected = (item: DropdownButtonItem) => {
    if (typeof modelValue.value === 'undefined' && modelValue.value === null) return false
    return modelValue.value === item.value
  }

  const isLoading = ref(true)

  onMounted(() => {
    isLoading.value = false
  })
</script>

<template>
  <div v-if="!isLoading">
    <ButtonDefault
      :icon="icon ?? ''"
      :severity="severity"
      :size="size"
      :label="label"
      outlined
      @click="toggle" />
    <Popover
      v-if="!multiselect"
      ref="op"
      class="!p-0 text-sm"
      :pt="{
    content: {
      style: { padding: '0.5rem' }
    }
  }">
      <ul>
        <li
          v-for="option in values"
          :key="option.label"
          :class="[
            'h-8 px-1 items-center flex cursor-pointer w-full',
            (isSelected(option) ? ' font-bold bg-slate-100' : ' hover:bg-gray-100'),
            (option.disabled) ? 'cursor-not-allowed pointer-events-none bg-gray-50 text-gray-500' : 'hover:bg-gray-100',
        ]"

          @click="option.disabled ? undefined : onListitemClick(option)">
          <div class="flex items-center justify-between w-full">
            <div class="flex items-center">
              <component v-if="option.icon" :is="option.icon" class="mr-1" />
              <span class="inline-block align-middle">{{ option.label }}</span>
            </div>
            <component v-if="selectedItemIcon && isSelected(option)" :is="selectedItemIcon" />
          </div>
        </li>
      </ul>
    </Popover>
    <Popover
      v-else
      ref="op"
      class="!p-0 text-sm"
      :pt="{
    content: {
      style: { padding: '0' }
    }
  }">
      <ul v-if="modelValue">
        <li
          v-for="(option, index) in values"
          :key="option.label"
          :class="'h-8' + (isSelected(option) ? ' font-bold bg-primary-100' : ' hover:bg-gray-100')"
          class="flex items-center gap-2 w-full cursor-pointer px-1"
          @click="onListitemClick(option)">
          <Checkbox
            v-model="modelValue[option.value as string]"
            :input-id="option.value as string"
            binary
            class="shrink-0 ml-1"
            @click="onListitemClick(option)" />
          <span class="break-words">{{ option.label }}</span>
        </li>
      </ul>
    </Popover>
  </div>
</template>
